<script setup lang="ts">
type Type = 'button' | 'text'

type Size = 'sm' | 'md'

type Props = {
  type?: Type
  size?: Size
  isSingle?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  type: 'button',
  size: 'md',
})

const componentClasses = computed(() => {
  const classes = [
    'app-tabs',
    `app-tabs--type-${props.type}`,
    `app-tabs--size-${props.size}`,
  ]

  if (props.isSingle) classes.push('app-tabs--single')

  return classes
})
</script>

<template>
  <v-tabs :class="componentClasses" v-bind="$attrs">
    <slot />
  </v-tabs>
</template>

<style lang="scss" scoped>
.app-tabs {
  :deep(.v-tab) {
    text-transform: none;
    letter-spacing: -0.01em;
  }

  :deep(.v-btn__overlay),
  :deep(.v-btn__underlay) {
    display: none !important;
  }

  :deep(.v-slide-group__prev),
  :deep(.v-slide-group__next) {
    display: none !important;
  }

  &--single {
    background-color: transparent !important;

    :deep(.v-tab) {
      width: 100% !important;
      cursor: auto !important;
      background-color: transparent !important;
      box-shadow: none !important;
    }

    :deep(.v-slide-group__content) {
      display: block !important;
    }

    :deep(.v-tab__slider) {
      box-shadow: none !important;
    }

    :deep(.v-ripple__container) {
      display: none !important;
    }
  }

  &--size-sm {
    height: 32px;

    :deep(.v-tab) {
      min-width: 30px !important;
      height: 26px !important;
      padding: 0 8px !important;
      font-size: 12px;
    }
  }

  &--size-md {
    height: 40px;

    :deep(.v-tab) {
      min-width: 60px !important;
      height: 34px !important;
      padding: 0 10px !important;
      font-size: 14px;
    }
  }

  &--type-button {
    background-color: var(--ui-grey);
    border-radius: 100px;

    :deep(.v-slide-group__content) {
      padding: 3px;
    }

    :deep(.v-tab) {
      flex: 0 1 auto;
      width: 100%;
      font-weight: 600;
      line-height: 19px;
      color: var(--ui-text-primary);
      border-radius: 100px !important;
    }

    :deep(.v-tab--selected) {
      font-weight: 800;
    }

    :deep(.v-tab__slider) {
      z-index: -1;
      height: 100%;
      background-color: var(--ui-white) !important;
      border-radius: 100px !important;
      box-shadow: 0 3px 1px 0 rgba(0, 0, 0, 0.04),
        0 3px 8px 0 rgba(0, 0, 0, 0.12);
    }
  }

  &--type-text {
    height: 40px;

    :deep(.v-slide-group__content) {
      gap: 24px;
      border-bottom: 1px solid rgba(#2c2f39, 0.3);
    }

    :deep(.v-tab) {
      align-items: flex-start;
      min-width: auto !important;
      height: 40px !important;
      padding: 0 !important;
      font-size: 16px !important;
      font-weight: 600;
      line-height: 21px;
      color: var(--ui-text-secondary);
      text-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    }

    :deep(.v-tab--selected) {
      font-weight: 800;
      color: var(--ui-text-primary);
    }

    :deep(.v-tab__slider) {
      background-color: var(--ui-primary) !important;
    }
  }
}
</style>
